/**
 * Zod schema for the `Batch` persistence shape + `batch_recipes` junction.
 *
 * Mirrors `../types/batch.ts`. Spec authority: `architecture.md §4.7`
 * `CREATE TABLE batches` + `CREATE TABLE batch_recipes`.
 */

import { z } from "zod";
import type { Batch, BatchRecipeMember, BatchStatus } from "../types/batch.js";
import { UUID_RE, HANDLE_SLUG_RE } from "../urls/types.js";

const uuid = z.string().regex(UUID_RE);
const slug = z.string().regex(HANDLE_SLUG_RE);
const timestamp = z.number().int().nonnegative();

export const BatchStatusSchema: z.ZodType<BatchStatus> = z.enum(["open", "closed", "archived"]);

export const BatchSchema = z
  .object({
    uuid,
    slug,
    name: z.string(),
    recipeUuid: uuid.nullable().optional(),
    bodyMarkdown: z.string(),
    status: BatchStatusSchema,
    batchKindUuid: uuid.nullable().optional(),
    createdAt: timestamp,
    updatedAt: timestamp,
    closedAt: timestamp.nullable().optional(),
  })
  .strict();

export const BatchRecipeMemberSchema = z
  .object({
    batchUuid: uuid,
    recipeUuid: uuid,
    addedAt: timestamp,
  })
  .strict();

type _AssertBatch =
  z.infer<typeof BatchSchema> extends Batch
    ? Batch extends z.infer<typeof BatchSchema>
      ? true
      : never
    : never;
type _AssertBatchRecipeMember =
  z.infer<typeof BatchRecipeMemberSchema> extends BatchRecipeMember
    ? BatchRecipeMember extends z.infer<typeof BatchRecipeMemberSchema>
      ? true
      : never
    : never;
export type __BatchSchemaAsserts = [_AssertBatch, _AssertBatchRecipeMember];
